import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { PlusIcon } from 'lucide-react';

import { SharedSpinner } from '@codes/ui-kit';

import { AccessKeyCard } from '../components/AccessKeyCard';
import { AccessKeyDeleteDialog } from '../components/AccessKeyDeleteDialog';
import { AccessKeyEmptyState } from '../components/AccessKeyEmptyState';
import { AccessKeyFormDialog } from '../components/AccessKeyFormDialog';
import { AccessKeySuccessDialog } from '../components/AccessKeySuccessDialog';
import { useAccessKeys } from '../hooks/useAccessKeys';

export const AccessKeysPage = () => {
    const { t } = useTranslation();

    const {
        keysData,
        isKeysLoading,
        isFormDialogOpen,
        setIsFormDialogOpen,
        dialogMode,
        selectedKey,
        isSuccessDialogOpen,
        setIsSuccessDialogOpen,
        newlyCreatedCredential,
        isDeleteDialogOpen,
        setIsDeleteDialogOpen,
        keyToDelete,
        isDeleting,
        handleCreate,
        handleEdit,
        handleDelete,
        confirmDelete,
        handleFormSuccess,
    } = useAccessKeys();

    if (isKeysLoading) {
        return (
            <div className="flex items-center justify-center h-[calc(100vh-200px)]">
                <SharedSpinner />
            </div>
        );
    }

    const keys = keysData?.data || [];

    return (
        <div className="flex-1 bg-background min-h-0 w-full flex flex-col">
            <div className="flex-1 py-[34px] px-[68px] max-md:px-4 max-md:pt-6">
                <div className="mx-auto">
                    <div className="flex items-center justify-between mb-8">
                        <h1 className="text-2xl font-bold">{t('accessKeys.title')}</h1>
                        <Button onClick={handleCreate}>
                            <PlusIcon className="mr-2 h-4 w-4" />
                            {t('accessKeys.createNew')}
                        </Button>
                    </div>

                    {keys.length === 0 ? (
                        <AccessKeyEmptyState onCreateClick={handleCreate} />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {keys.map(key => (
                                <AccessKeyCard
                                    key={key.id || key.accessKey}
                                    data={key}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <AccessKeyFormDialog
                open={isFormDialogOpen}
                onOpenChange={setIsFormDialogOpen}
                mode={dialogMode}
                keyData={selectedKey}
                onSuccess={handleFormSuccess}
            />

            {newlyCreatedCredential && (
                <AccessKeySuccessDialog
                    open={isSuccessDialogOpen}
                    onOpenChange={setIsSuccessDialogOpen}
                    credential={newlyCreatedCredential}
                />
            )}

            {keyToDelete && (
                <AccessKeyDeleteDialog
                    open={isDeleteDialogOpen}
                    onOpenChange={setIsDeleteDialogOpen}
                    keyName={keyToDelete.userName || keyToDelete.id || 'credential'}
                    isDeleting={isDeleting}
                    onConfirm={confirmDelete}
                />
            )}
        </div>
    );
};
